import { useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { RefreshButton } from "./RefreshButton";
import { fmt, short } from "../format";
import type { Scraper } from "../types";

export type ScraperFilter = "all" | "active" | "paused" | "disabled";
const FILTERS: ScraperFilter[] = ["all", "active", "paused", "disabled"];

/**
 * Every scraper across every client — operator-only (GET /scrapers spans all tenants for an admin).
 * The "paused → scrapers" drill from Overview lands here pre-filtered. Kintsugi: active is plain ink,
 * paused is an iron-oxide crack, disabled is quiet.
 */
export function FleetScrapers({ initialStatus = "all" }: { initialStatus?: ScraperFilter }) {
  const [filter, setFilter] = useState<ScraperFilter>(initialStatus);
  const { data, loading, error, reload } = useAsync<Scraper[]>(() => api("/scrapers"), []);
  // /tenants is paginated: `{ clients: [...] }`, NOT `{ tenants: [...] }`.
  const { data: tenantPage } = useAsync<{ clients: { id: string; name: string }[] }>(
    () => api("/tenants?pageSize=200"),
    [],
  );
  const clientName = (id: string) => tenantPage?.clients.find((t) => t.id === id)?.name ?? short(id);

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  const all = data ?? [];
  const scrapers = filter === "all" ? all : all.filter((s) => s.status === filter);
  const countFor = (f: ScraperFilter) =>
    f === "all" ? all.length : all.filter((s) => s.status === f).length;

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Scrapers</h1>
        <RefreshButton onClick={reload} />
      </div>
      <p className="muted sm">Every scraper across all clients.</p>

      <label className="filter-select muted sm">
        Status
        <select value={filter} onChange={(e) => setFilter(e.target.value as ScraperFilter)}>
          {FILTERS.map((f) => (
            <option key={f} value={f}>
              {(f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)) + ` (${countFor(f)})`}
            </option>
          ))}
        </select>
      </label>

      {scrapers.length === 0 && (
        <div className="empty">{filter === "all" ? "No scrapers yet." : `No ${filter} scrapers.`}</div>
      )}

      {scrapers.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Scraper</th>
                <th>Status</th>
                <th>Schedule</th>
                <th>Last run</th>
                <th>Next run</th>
              </tr>
            </thead>
            <tbody>
              {scrapers.map((s) => (
                <tr key={s.id} className={s.status === "paused" ? "is-fail" : undefined}>
                  <td>{clientName(s.tenant_id)}</td>
                  <td>{s.name}</td>
                  <td>
                    <span
                      className={s.status === "paused" ? "err" : s.status === "disabled" ? "muted" : ""}
                    >
                      {s.status}
                    </span>
                  </td>
                  <td className="mono muted">{s.schedule ?? "—"}</td>
                  <td className="nowrap">
                    {s.last_run_at ? fmt(s.last_run_at) : "—"}
                    {s.last_run_status ? (
                      <>
                        {" "}
                        <span className={`pill ${s.last_run_status}`}>{s.last_run_status}</span>
                      </>
                    ) : (
                      ""
                    )}
                  </td>
                  <td className="nowrap muted">{s.next_run_at ? fmt(s.next_run_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
